import type { Block, BlockSegment } from '../api/client.ts';

/**
 * The editor's timeline.
 *
 * The editor spans the whole half hour in absolute clock time rather than one
 * segment's file. A block routinely holds several segments — four, in the
 * archive this was written against — and binding the editor to one of them put
 * the rest out of reach: a partly-recorded block could not be trimmed to the
 * part that survived, and the block being recorded could not be cut at all.
 *
 * Working in absolute time is also what spec §9 asks for, and it means a
 * selection maps straight onto an export request with no conversion.
 */

export interface Span {
  fromMs: number;
  toMs: number;
}

export interface PlacedSegment {
  segment: BlockSegment;
  /** Where this segment's usable audio sits on the clock. */
  fromMs: number;
  toMs: number;
  /** Start of the file itself, which is where its peaks are indexed from. */
  fileStartMs: number;
}

export function blockWindow(block: Block): Span {
  return {
    fromMs: new Date(block.block_start_at).getTime(),
    toMs: new Date(block.block_end_at).getTime(),
  };
}

/** Every segment of the block, positioned on the clock, earliest first. */
export function placeSegments(block: Block): PlacedSegment[] {
  return block.segments
    .map((segment) => {
      const fileStartMs = new Date(segment.started_at).getTime();
      const fromMs = new Date(segment.covers_from).getTime();
      const toMs = segment.covers_to
        ? new Date(segment.covers_to).getTime()
        // Still recording: only what has been measured onto disk counts.
        : fileStartMs + (segment.duration_sec ?? 0) * 1000;
      return { segment, fromMs, toMs, fileStartMs };
    })
    .filter((placed) => placed.toMs > placed.fromMs)
    .sort((a, b) => a.fromMs - b.fromMs);
}

/**
 * The parts of the block that actually hold audio, merged and clipped to the
 * window. Anything outside these is a gap the producer cannot select.
 */
export function coveredSpans(block: Block): Span[] {
  const window = blockWindow(block);
  const spans: Span[] = [];

  for (const placed of placeSegments(block)) {
    const from = Math.max(placed.fromMs, window.fromMs);
    const to = Math.min(placed.toMs, window.toMs);
    if (to <= from) continue;

    const last = spans[spans.length - 1];
    // Segments overlap after a reconnect, so merge rather than list twice.
    if (last && from <= last.toMs) last.toMs = Math.max(last.toMs, to);
    else spans.push({ fromMs: from, toMs: to });
  }
  return spans;
}

/** The segment holding a given instant, and how far into its file that is. */
export function segmentAt(
  block: Block,
  atMs: number,
): { segment: BlockSegment; offsetSec: number } | null {
  for (const placed of placeSegments(block)) {
    if (atMs >= placed.fromMs && atMs < placed.toMs) {
      return {
        segment: placed.segment,
        // Offsets are into the file, which begins before its usable audio when
        // the head duplicates the previous segment.
        offsetSec: (atMs - placed.fileStartMs) / 1000,
      };
    }
  }
  return null;
}

/** True when every instant between two times holds audio. */
export function isFullyCovered(block: Block, fromMs: number, toMs: number): boolean {
  const spans = coveredSpans(block);
  return spans.some((span) => fromMs >= span.fromMs - 1 && toMs <= span.toMs + 1);
}

/** The first instant of the block that has audio, for opening the editor. */
export function firstCoveredMs(block: Block): number | null {
  return coveredSpans(block)[0]?.fromMs ?? null;
}

export function absToX(atMs: number, window: Span, width: number): number {
  const span = window.toMs - window.fromMs;
  if (span <= 0) return 0;
  return ((atMs - window.fromMs) / span) * width;
}

export function xToAbs(x: number, window: Span, width: number): number {
  if (width <= 0) return window.fromMs;
  const span = window.toMs - window.fromMs;
  const at = window.fromMs + (x / width) * span;
  return Math.min(window.toMs, Math.max(window.fromMs, at));
}

/**
 * Pulls an instant onto the nearest audio. Dragging across a gap should land
 * the handle on something real rather than select silence that does not exist.
 */
export function snapToCoverage(block: Block, atMs: number): number {
  const spans = coveredSpans(block);
  if (spans.length === 0) return atMs;

  for (const span of spans) {
    if (atMs >= span.fromMs && atMs <= span.toMs) return atMs;
  }
  let best = spans[0]!.fromMs;
  let bestDistance = Infinity;
  for (const span of spans) {
    for (const edge of [span.fromMs, span.toMs]) {
      const distance = Math.abs(edge - atMs);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = edge;
      }
    }
  }
  return best;
}

/**
 * The block's audio as a single unbroken span, or null when it is in pieces.
 *
 * "Download the whole half hour" can only mean one file, and a block with an
 * outage in it cannot produce one without inventing the missing audio. Those
 * blocks are cut by hand instead, which is what the editor is for.
 */
export function wholeBlockSpan(block: Block): Span | null {
  const spans = coveredSpans(block);
  return spans.length === 1 ? spans[0]! : null;
}
